import { call, takeLatest, delay, select } from 'redux-saga/effects';
import { AxiosError } from 'axios';
import * as api from '@api/DXAReadings';
import { actions, selectDXAReadingsState } from './DXAReadings.slice';
import {
  DXAReadingsFetchAction,
  DXAReadingsSyncAction,
  DXARemoveSelectedReadingAction,
  DXASendSelectedReadingAction,
  DXAReadingErrorResponse,
} from './DXAReadings.types';
import { Resp, dispatch } from '../helpers';
import { runToast } from '../tools';
import { ExecutionStatus } from '@dynamika-shared/types/executions';

const PING_INTERVAL = 30000; // 30 seconds

export function* fetchDXAReadingsWorker(action: DXAReadingsFetchAction) {
  try {
    const data: Resp<typeof api.fetchDXAVisitData> = yield call(
      api.fetchDXAVisitData,
      action.payload.clinicalTrialId,
      action.payload.taskId
    );
    yield dispatch(actions.fetchDXAReadingsSuccess(data));
  } catch (error) {
    yield dispatch(actions.fetchDXAReadingsFailure(error as Error));
  }
}

export function* fetchDXAWorkstationReadingsWorker(
  action: DXAReadingsFetchAction
) {
  try {
    const data: Resp<typeof api.fetchDXAWorkstationData> = yield call(
      api.fetchDXAWorkstationData,
      action.payload.clinicalTrialId,
      action.payload.taskId
    );
    yield dispatch(actions.fetchDXAWorkstationReadingsSuccess(data));
  } catch (error) {
    yield dispatch(actions.fetchDXAWorkstationReadingsFailure(error as Error));
  }
}

export function* syncWorkstationWorker(action: DXAReadingsSyncAction) {
  try {
    yield call(
      api.syncWorkstationReadings,
      action.payload.clinicalTrialId,
      action.payload.taskId
    );
    yield dispatch(actions.getSyncStatus(action.payload));
  } catch (error) {
    console.error('Error syncing workstation readings', error);
  }
}

export function* sendToReadingsWorker(action: DXASendSelectedReadingAction) {
  try {
    const { clinicalTrialId, data, taskId } = action.payload;
    const state: ReturnType<typeof selectDXAReadingsState> = yield select(
      selectDXAReadingsState
    );

    const prev = state.visitData?.dxaScores || [];
    const dataToSend = [...prev, ...data];
    yield call(
      api.putWorkstationDataToVisit,
      clinicalTrialId,
      taskId,
      dataToSend
    );
    yield call(runToast, 'success', 'Values have been saved for reading');
    yield dispatch(actions.fetchDXAReadings({ clinicalTrialId, taskId }));
  } catch (error) {
    yield call(
      runToast,
      'error',
      `${
        ((error as AxiosError)?.response?.data as DXAReadingErrorResponse)
          ?.detail
      }`
    );
    console.error('Error syncing workstation readings', error);
  }
}

export function* removeFromReadingsWorker(
  action: DXARemoveSelectedReadingAction
) {
  try {
    const { visitData }: ReturnType<typeof selectDXAReadingsState> =
      yield select(selectDXAReadingsState);

    const values = (visitData?.dxaScores || []).filter(
      v => v.id !== action.payload.data.id
    );

    yield call(
      api.putWorkstationDataToVisit,
      action.payload.clinicalTrialId,
      action.payload.taskId,
      values
    );
    yield call(runToast, 'success', 'Item has been removed from readings');
    yield dispatch(actions.fetchDXAReadings(action.payload));
  } catch (error) {
    yield call(runToast, 'error', 'Oops, something went wrong');
    console.error('Error syncing workstation readings', error);
  }
}

export function* getSyncStatusWorker(action: DXAReadingsSyncAction) {
  while (true) {
    try {
      const statusResult: Resp<typeof api.getSyncWorkstationReadingsStatus> =
        yield call(
          api.getSyncWorkstationReadingsStatus,
          action.payload.clinicalTrialId,
          action.payload.taskId
        );

      const last = [...statusResult].sort((a, b) => {
        return new Date(b.started).getTime() - new Date(a.started).getTime();
      })[0];

      const isLastStatusInProgress = statusResult.some(
        status => status.status === ExecutionStatus.IN_PROGRESS
      );

      if (last) {
        yield dispatch(
          actions.syncWorkstationStatus({
            lastSync: last.finished,
            status: isLastStatusInProgress ? ExecutionStatus.IN_PROGRESS : last.status,
          })
        );
      }

      if (last?.status === ExecutionStatus.COMPLETED) {
        yield dispatch(actions.fetchDXAWorkstationReadings(action.payload));
      }

      if (!isLastStatusInProgress) {
        break;
      }
      yield delay(PING_INTERVAL);
    } catch (error) {
      console.error('Error syncing workstation status', error);
      break;
    }
  }
}

export function* DXAReadingsSaga() {
  yield takeLatest(actions.fetchDXAReadings.type, fetchDXAReadingsWorker);
  yield takeLatest(
    actions.fetchDXAWorkstationReadings.type,
    fetchDXAWorkstationReadingsWorker
  );

  yield takeLatest(actions.syncWorkstation.type, syncWorkstationWorker);
  yield takeLatest(actions.getSyncStatus.type, getSyncStatusWorker);
  yield takeLatest(actions.sendToReadings.type, sendToReadingsWorker);
  yield takeLatest(
    actions.removeSelectedReading.type,
    removeFromReadingsWorker
  );
}
